import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { 
  Upload, Image, Trash2, Copy, Check, Loader2, 
  FolderOpen, X, Camera, Users, ShoppingBag, Film, User
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";

const PhotosManager = () => {
  const { authAxios } = useAuth();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [copiedId, setCopiedId] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadCategory, setUploadCategory] = useState("general");

  const categories = [
    { value: "general", label: "General", icon: Image },
    { value: "players", label: "Players", icon: Users },
    { value: "media", label: "Media Gallery", icon: Film },
    { value: "merchandise", label: "Merchandise", icon: ShoppingBag },
    { value: "about", label: "About / Coach", icon: User },
  ];

  useEffect(() => {
    fetchUploads();
  }, [selectedCategory]);

  const fetchUploads = async () => {
    try {
      const params = selectedCategory !== "all" ? `?category=${selectedCategory}` : "";
      const response = await authAxios.get(`/uploads${params}`);
      setUploads(response.data);
    } catch (e) {
      console.error("Error fetching uploads:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', uploadCategory);

        await authAxios.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        successCount++;
      } catch (e) {
        errorCount++;
        console.error("Upload error:", e);
      }
    }

    setUploading(false);
    fileInputRef.current.value = '';

    if (successCount > 0) {
      toast.success(`${successCount} photo(s) uploaded!`);
      fetchUploads();
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} photo(s) failed to upload`);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this photo?")) return;

    try {
      await authAxios.delete(`/uploads/${id}`);
      toast.success("Photo deleted");
      fetchUploads();
    } catch (e) {
      toast.error("Failed to delete photo");
    }
  };

  const copyUrl = (upload) => {
    const fullUrl = `${API.replace('/api', '')}${upload.url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedId(upload.id);
    toast.success("URL copied!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getCategoryIcon = (cat) => {
    const found = categories.find(c => c.value === cat);
    return found ? found.icon : Image;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="font-orbitron text-xl text-white flex items-center gap-2">
          <Camera className="w-5 h-5 text-neon-blue" />
          Photo Library ({uploads.length})
        </h2>
      </div>

      {/* Upload Section */}
      <div className="cyber-card p-6 border-neon-blue/30">
        <h3 className="font-orbitron text-sm text-neon-blue mb-4 flex items-center gap-2">
          <Upload className="w-4 h-4" />
          UPLOAD NEW PHOTOS
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="space-y-2">
            <Label className="font-orbitron text-xs text-gray-400">Category</Label>
            <Select value={uploadCategory} onValueChange={setUploadCategory}>
              <SelectTrigger className="bg-void-black border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-void-black border-white/10">
                {categories.map(cat => (
                  <SelectItem key={cat.value} value={cat.value} className="text-white">
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              id="photo-upload"
            />
            <label
              htmlFor="photo-upload"
              className={`cyber-btn w-full py-4 cursor-pointer flex items-center justify-center gap-2 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
            >
              {uploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>UPLOADING...</span>
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  <span>SELECT PHOTOS TO UPLOAD</span>
                </>
              )}
            </label>
            <p className="font-rajdhani text-xs text-gray-500 mt-2 text-center">
              Supports JPG, PNG, GIF, WEBP • Max 5MB per file • Select multiple files
            </p>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-orbitron text-xs text-gray-400">FILTER:</span>
        <button
          onClick={() => setSelectedCategory("all")}
          className={`px-3 py-1 text-xs font-orbitron transition-colors ${
            selectedCategory === "all" 
              ? "bg-neon-blue text-black" 
              : "bg-white/5 text-gray-400 hover:bg-white/10"
          }`}
        >
          ALL
        </button>
        {categories.map(cat => {
          const Icon = cat.icon;
          return (
            <button
              key={cat.value}
              onClick={() => setSelectedCategory(cat.value)}
              className={`px-3 py-1 text-xs font-orbitron transition-colors flex items-center gap-1 ${
                selectedCategory === cat.value 
                  ? "bg-neon-pink text-white" 
                  : "bg-white/5 text-gray-400 hover:bg-white/10"
              }`}
            >
              <Icon className="w-3 h-3" />
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* Photos Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-neon-blue animate-spin" />
        </div>
      ) : uploads.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {uploads.map(upload => {
            const CategoryIcon = getCategoryIcon(upload.category);
            const fullUrl = `${API.replace('/api', '')}${upload.url}`;
            
            return (
              <div key={upload.id} className="cyber-card overflow-hidden group">
                <div className="aspect-square relative bg-white/5">
                  <img
                    src={fullUrl}
                    alt={upload.original_name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23111" width="100" height="100"/><text x="50%" y="50%" fill="%23666" text-anchor="middle" dy=".3em" font-family="sans-serif" font-size="12">Error</text></svg>';
                    }}
                  />
                  
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={() => copyUrl(upload)}
                      className="p-2 bg-neon-blue text-black hover:bg-neon-blue/80 transition-colors"
                      title="Copy URL"
                    >
                      {copiedId === upload.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleDelete(upload.id)}
                      className="p-2 bg-red-500 text-white hover:bg-red-600 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Category Badge */}
                  <div className="absolute top-2 left-2 bg-black/70 px-2 py-1">
                    <CategoryIcon className="w-3 h-3 text-neon-pink" />
                  </div>
                </div>

                <div className="p-2">
                  <p className="font-rajdhani text-xs text-white truncate" title={upload.original_name}>
                    {upload.original_name}
                  </p>
                  <p className="font-rajdhani text-xs text-gray-500">
                    {formatSize(upload.size)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 cyber-card">
          <Image className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="font-rajdhani text-gray-500">No photos uploaded yet.</p>
          <p className="font-rajdhani text-xs text-gray-600 mt-2">
            Upload photos above to get started!
          </p>
        </div>
      )}

      {/* Usage Instructions */}
      <div className="cyber-card p-4 border-white/10">
        <h4 className="font-orbitron text-xs text-gray-400 mb-2">HOW TO USE</h4>
        <ol className="font-rajdhani text-sm text-gray-500 space-y-1 list-decimal list-inside">
          <li>Upload photos using the button above</li>
          <li>Hover over any photo and click the <Copy className="w-3 h-3 inline" /> icon to copy its URL</li>
          <li>Paste the URL in any image field (Players, Merchandise, Media, About section)</li>
        </ol>
      </div>
    </div>
  );
};

export default PhotosManager;
